import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';

const FAQSection = () => {
  const [openIndex, setOpenIndex] = useState(0);

  const faqs = [
    {
      question: "How does the anonymous profile system work?",
      answer: `When you create a profile on Workfolio, we automatically anonymize all personal identifying information. Your name, photo, and contact details are replaced with an anonymous identifier like "Anonymous Candidate #1234". Employers can see your skills, experience, education, and work preferences, but cannot identify who you are until they pay to unlock your profile.`
    },
    {
      question: "Is Workfolio really free for job seekers?",
      answer: `Yes, absolutely! Job seekers can create profiles, browse opportunities, and be discovered by employers completely free of charge. There are no hidden fees, subscription costs, or premium features that require payment. Our revenue comes from employers who pay to unlock candidate profiles when they find someone they want to contact.`
    },
    {
      question: "How much does it cost for employers to unlock a profile?",
      answer: `Employers pay 149 NOK (approximately €14) to unlock each candidate profile. This one-time payment reveals the candidate's full contact information and allows direct communication. There are no monthly subscriptions or setup fees - employers only pay when they find candidates worth contacting.`
    },
    {
      question: "What information do employers see before unlocking?",
      answer: `Before unlocking, employers can see all professional information: skills and proficiency levels, work experience, education, certifications, portfolio links, location preferences, availability, work permit status, languages, and salary expectations. They cannot see names, photos, contact information, or any personally identifying details.`
    },
    {
      question: "How do you ensure GDPR compliance and data privacy?",
      answer: `We take privacy seriously and are fully GDPR compliant. You have complete control over your data - you can update, download, or delete your profile at any time. We use advanced encryption for all data storage and transmission. Personal information is only revealed after explicit employer payment and your continued consent to be contacted.`
    },
    {
      question: "Can I control who sees my profile?",
      answer: `Yes! You can set your profile to active or inactive at any time. When active, employers can find you through searches. When inactive, your profile is hidden from all searches. You'll also receive notifications when employers view or unlock your profile, giving you full transparency about who's interested in your background.`
    },
    {
      question: "What happens after an employer unlocks my profile?",
      answer: `Once unlocked, the employer gains access to your contact information and can reach out directly via email or phone. You'll receive a notification about the unlock, including the company name and role they're hiring for. You're under no obligation to respond, and you maintain full control over the conversation.`
    },
    {
      question: "How does Workfolio reduce hiring bias?",
      answer: `By removing names, photos, and demographic identifiers from the initial screening process, employers must focus solely on qualifications, skills, and experience. This eliminates unconscious bias based on gender, ethnicity, age, or appearance. Studies show this approach leads to more diverse hiring and better job matches based on actual capability.`
    }
  ];

  const toggleFAQ = (index) => {
    setOpenIndex(openIndex === index ? -1 : index);
  };

  return (
    <section className="py-20 lg:py-32 bg-muted/20">
      <div className="container mx-auto px-6 lg:px-8">
        <div className="text-center mb-16">
          <div className="inline-flex items-center px-4 py-2 rounded-full bg-destructive/10 text-destructive text-sm font-medium mb-6">
            <Icon name="HelpCircle" size={16} className="mr-2" />
            Frequently Asked Questions
          </div>
          
          <h2 className="text-3xl lg:text-5xl font-bold text-foreground mb-6">
            Got Questions?
          </h2>
          
          <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
            Find answers to common questions about how Workfolio works, 
            pricing, privacy, and our anonymous recruitment process.
          </p>
        </div>
        
        <div className="max-w-4xl mx-auto">
          <div className="space-y-4">
            {faqs?.map((faq, index) => (
              <div 
                key={index}
                className="bg-card rounded-2xl border border-border shadow-elevation overflow-hidden transition-all duration-300 hover:shadow-elevation-lg"
              >
                <button
                  onClick={() => toggleFAQ(index)}
                  className="w-full px-6 lg:px-8 py-6 text-left flex items-center justify-between hover:bg-muted/30 transition-colors"
                  aria-expanded={openIndex === index}
                >
                  <h3 className="text-lg font-semibold text-foreground pr-4">
                    {faq?.question}
                  </h3>
                  
                  <div className={`flex-shrink-0 w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center transition-transform duration-300 ${
                    openIndex === index ? 'rotate-180' : ''
                  }`}>
                    <Icon name="ChevronDown" size={20} className="text-primary" />
                  </div>
                </button>
                
                <div className={`overflow-hidden transition-all duration-300 ${
                  openIndex === index ? 'max-h-96 opacity-100' : 'max-h-0 opacity-0'
                }`}>
                  <div className="px-6 lg:px-8 pb-6">
                    <div className="pt-4 border-t border-border">
                      <p className="text-muted-foreground leading-relaxed">
                        {faq?.answer}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
          
          {/* Contact CTA */}
          <div className="mt-12 text-center">
            <div className="bg-card rounded-2xl p-8 shadow-elevation border border-border">
              <h3 className="text-xl font-semibold text-foreground mb-4">
                Still have questions?
              </h3>
              <p className="text-muted-foreground mb-6">
                Our support team is here to help you get started with Workfolio.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <a
                  href="/contact"
                  className="inline-flex items-center justify-center px-6 py-3 bg-primary text-primary-foreground rounded-lg font-medium hover:bg-primary/90 transition-colors"
                >
                  <Icon name="MessageCircle" size={20} className="mr-2" />
                  Contact Support
                </a>
                <a
                  href="mailto:support@workfolio.no"
                  className="inline-flex items-center justify-center px-6 py-3 border border-border rounded-lg font-medium text-foreground hover:bg-muted/50 transition-colors"
                >
                  <Icon name="Mail" size={20} className="mr-2" />
                  support@workfolio.no
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default FAQSection;